import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
  input,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ChartData, ChartOptions, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import {
  BillingHistoryResponse,
  computeDateRange,
} from '../../../models/billing-history.models';
import { ResourceService } from '../../../services/resource.service';

@Component({
  selector: 'app-resource-billing-summary',
  standalone: true,
  imports: [MatProgressSpinnerModule, MatIconModule, BaseChartDirective],
  templateUrl: './resource-billing-summary.component.html',
  styleUrls: ['./resource-billing-summary.component.scss'],
})
export class ResourceBillingSummaryComponent implements OnInit, OnDestroy {
  private resourceService = inject(ResourceService);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly employeeId = input.required<string>();

  isLoading = false;
  monthlyAverageBilledHours: number | null = null;
  currentYearToDateBilledHours: number | null = null;
  forecastedBilledHoursThisYear: number | null = null;
  forecasted12MonthBilledHours: number | null = null;

  // Composite forecast chart. Two views of the same data, switched via the toggle:
  //  - cumulative: the server's running totals, as bars for completed months + a line that meets the
  //    last bar then rises to the year-end forecast.
  //  - exact: per-month bars + a line matching them, flattening at the average for forecast months.
  hasChart = false;
  chartMode: 'cumulative' | 'exact' = 'exact';
  readonly chartType: ChartType = 'bar';
  chartData: ChartData<'bar' | 'line'> = { labels: [], datasets: [] };
  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { intersect: false, mode: 'index' },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Billable Hours' } },
    },
    plugins: {
      // reverse so the legend reads Forecast, then Actual.
      legend: { display: true, position: 'bottom', reverse: true },
    },
  };

  // Source data for the chart, stashed so toggling views doesn't refetch.
  private monthly: (number | null)[] = [];
  private monthlyAverage = 0;
  private cumulative: number[] = [];

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.loadBillingSummary();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Format hours for display. Shows "-" only when the value is missing
   * (null/undefined); a real 0 from the API is shown as "0".
   */
  formatHours(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '-';
    }
    // Format to 1 decimal place
    return parseFloat(value.toFixed(1)).toString();
  }

  /** Read a global CSS custom property (from _variables.scss) so chart colors stay in sync. */
  private cssVar(name: string): string {
    return getComputedStyle(this.host.nativeElement).getPropertyValue(name).trim();
  }

  /**
   * Stash the monthly breakdown, average and running totals, and render the chart for the current
   * view. Toggling the view re-renders from this stashed data without a refetch.
   */
  private buildChart(
    monthly: (number | null)[],
    monthlyAverage: number,
    cumulative: number[],
  ): void {
    this.monthly = monthly ?? [];
    this.monthlyAverage = monthlyAverage;
    this.cumulative = cumulative ?? [];
    this.hasChart = this.monthly.length > 0;
    if (this.hasChart) {
      this.applyChartMode();
    }
  }

  /** Switch between the cumulative and per-month views (no refetch). */
  setChartMode(mode: 'cumulative' | 'exact'): void {
    if (this.chartMode === mode) {
      return;
    }
    this.chartMode = mode;
    if (this.hasChart) {
      this.applyChartMode();
    }
  }

  /**
   * Build the bar + line datasets from the stashed data for the active view.
   * Completed months = non-null entries; from the first null on, the bar drops out and the
   * line follows the forecast. In cumulative mode both series carry the server's running
   * totals; in exact mode they carry per-month values, the line using the monthly average.
   */
  private applyChartMode(): void {
    const labels = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ];
    const round1 = (n: number) => Math.round(n * 10) / 10;
    const cumulativeMode = this.chartMode === 'cumulative';

    const bar: (number | null)[] = [];
    const line: (number | null)[] = [];
    let forecasting = false;

    for (let i = 0; i < labels.length; i++) {
      const value = this.monthly[i];
      const isActual = !forecasting && value != null;
      if (!isActual) {
        // Current month onward: no bar; the line follows the forecast.
        forecasting = true;
      }

      if (cumulativeMode) {
        // Not summed here: the months and average arrive rounded, so their running total drifts
        // a few tenths from the year-to-date and forecast tiles. The server's series is rounded
        // from the unrounded totals and lands on both.
        const point = this.cumulative[i] ?? null;
        bar.push(isActual ? point : null);
        line.push(point);
      } else {
        bar.push(isActual ? round1(value!) : null);
        line.push(round1(isActual ? value! : this.monthlyAverage));
      }
    }

    const barColor = this.cssVar('--silver-lake-blue');
    const lineColor = this.cssVar('--dark-gold');

    this.chartData = {
      labels,
      datasets: [
        {
          type: 'bar',
          label: cumulativeMode ? 'Actual (YTD)' : 'Actual',
          data: bar,
          backgroundColor: barColor,
          borderRadius: 4,
          maxBarThickness: 28,
          order: 2,
        },
        {
          type: 'line',
          label: 'Forecast',
          data: line,
          borderColor: lineColor,
          backgroundColor: lineColor,
          borderWidth: 2,
          pointRadius: 2,
          tension: 0.3,
          fill: false,
          order: 1,
        },
      ],
    };
  }

  private loadBillingSummary(): void {
    const employeeId = this.employeeId();
    if (!employeeId) {
      return;
    }

    // The forecast figures are a fixed annual projection independent of any
    // user-selected range, so this card always requests a trailing 12-month
    // window — enough history for the server's six-completed-month average.
    // The stat-tile labels in the template describe that methodology in words,
    // so changing the server-side calc means changing the copy with it.
    const { startDate, endDate } = computeDateRange({
      key: 'billing-summary-window',
      label: '',
      kind: 'rolling',
      days: 365,
    });

    this.isLoading = true;

    this.resourceService
      // billedTypes omitted => all types
      .getBillingHistory(employeeId, startDate, endDate)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: (response: BillingHistoryResponse) => {
          if (!response) {
            return;
          }
          // Preserve null when the API omits a field (renders as "-"); a real 0
          // is kept and shown as "0".
          this.monthlyAverageBilledHours =
            response.monthlyAverageBilledHours ?? null;
          this.currentYearToDateBilledHours =
            response.currentYearToDateBilledHours ?? null;
          this.forecastedBilledHoursThisYear =
            response.forecastedBilledHoursThisYear ?? null;
          this.forecasted12MonthBilledHours =
            response.forecasted12MonthBilledHours ?? null;
          this.buildChart(
            response.monthlyBilledHours ?? [],
            response.monthlyAverageBilledHours ?? 0,
            response.cumulativeBilledHours ?? [],
          );
        },
        error: () => {
          // Leave values at null (rendered as "-"); this card is non-critical chrome.
        },
      });
  }
}
